import {
  ArrowPathRoundedSquareIcon,
  CheckIcon,
} from '@heroicons/react/24/solid';
import { CheckCircleIcon, ChevronDownIcon } from '@heroicons/react/20/solid';
import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Dropdown, Menu } from '@/components/ui/daisy';
import { VersioningStatusEnum } from '@/enums/VersioningStatus';
import CoursePath from '@/models/CoursePath';
import useContPedStatus, { ValidationError } from './useContPedStatus';
import ValidationErrorHandler from './validation/ValidationErrorHandler';
import ContPedStatus from './PedContentStatus';
import { CacheManager } from '@/data/hook/query/v2/CacheFacade';
import { usePublishableLessons } from '@/hooks/lessons/usePublishableLessons';
import ConditionalWrapper from '../ui/custom/ConditionalWrapper';

type PedContentPublishActionsProps = {
  coursePath: CoursePath;
  cache: CacheManager<CoursePath>;
  onPublished: (coursePath: CoursePath) => void;
  toggle?: () => void;
};

type PublishMenuItem = {
  icon: ReactNode;
  label: string;
  onClick?: () => void;
  hidden?: boolean;
};

export default function PedContentPublishActions({
  coursePath,
  cache,
  onPublished,
  toggle,
}: PedContentPublishActionsProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.pedContentTab',
  });
  const [validationError, setValidationError] =
    useState<ValidationError | null>(null);

  const { validateAndPublish, isPending } = useContPedStatus({
    coursePath,
    cache,
    onInvalidate: setValidationError,
    onPublished,
  });

  const { hasPublishableLessons, isLoading } = usePublishableLessons(
    coursePath.slug,
  );

  const menuItems: PublishMenuItem[] = [
    {
      icon: <ArrowPathRoundedSquareIcon className="size-5" />,
      label: t('publishBeta'),
      onClick: () => validateAndPublish(VersioningStatusEnum.BETA),
      hidden: coursePath.status === VersioningStatusEnum.EDITING,
    },
    {
      icon: <CheckIcon className="size-5" />,
      label: t('publish'),
      onClick: toggle,
    },
  ];

  return (
    <div className="flex items-center gap-4">
      <ContPedStatus coursePath={coursePath} />
      <ConditionalWrapper
        condition={!hasPublishableLessons}
        wrapper={
          <div
            className="tooltip tooltip-left"
            data-tip={t('noVersioningLessons')}
          />
        }
      >
        <Dropdown position="bottom" align="end">
          <Dropdown.Trigger disabled={isPending || !hasPublishableLessons}>
            <Button
              color="primary"
              disabled={isPending || !hasPublishableLessons || isLoading}
              loading={isPending || isLoading}
            >
              <CheckCircleIcon className="size-3.5" />
              {t('publish')}
              <ChevronDownIcon className="size-3.5" />
            </Button>
          </Dropdown.Trigger>
          <Dropdown.Content className="mt-2">
            <Menu className="bg-base-100 shadow-lg rounded-box w-fit">
              {menuItems
                .filter(item => !item.hidden)
                .map((item, index) => (
                  <Menu.Item key={index} className="w-50 h-9">
                    <button
                      type="button"
                      className="flex items-center gap-2"
                      onClick={item.onClick}
                    >
                      {item.icon}
                      {item.label}
                    </button>
                  </Menu.Item>
                ))}
            </Menu>
          </Dropdown.Content>
          <ValidationErrorHandler
            error={validationError}
            onClose={() => setValidationError(null)}
          />
        </Dropdown>
      </ConditionalWrapper>
    </div>
  );
}
